/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @angular-eslint/no-output-native */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  computed,
  Directive,
  EventEmitter,
  inject,
  Injector,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { IHintWithIcon } from './../models/hint-with-icon.model';
import { IdGenerator } from '../services/id-generator.service';

@Directive()
export class InputDirective implements OnInit, OnChanges, ControlValueAccessor {
  @ViewChild(FormControlDirective, { static: true })
  formControlDirective: FormControlDirective;
  control: FormControl;
  qaAttributeHook: string | null = null;
  labelAsHtml = '';
  /**
   * Numerical min for number inputs
   *
   * Could move to text input
   */
  @Input() min: number | null;
  /**
   * Numerical min for number inputs
   *
   * Could move to text input
   */
  @Input() max: number | null;
  /**
   * Placeholder for input
   */
  @Input() placeholder: string | null;
  /**
   * Regex expression for validation
   */
  @Input() pattern: string | null;
  @Input() formControlName: string | undefined;
  @Input() formControl: FormControl | undefined;
  @Input() label: string | null;
  /**
   * Label that will be set on aria-label
   */
  @Input() ariaLabel: string | null;
  @Input() ariaLabelledBy: string | null;
  @Input() ariaDescribedBy: string | null;
  /**
   * Override for standard validation.
   *
   * If this is present and the form is invalid and touched,
   * this text will be rendered instead of the invalid validation.
   */
  @Input() errorText: string | null;
  /**
   * Custom error messages for validation errors.
   *
   * Given an object that maps a validation error key to a given
   * string, if the form is invalid, touched, and the validation
   * key is present in the map, the corresponding string will be
   * displayed.
   *
   * This does not supersede an errorText.
   */
  @Input() errorMessageMap: { [index: string]: string } | null;

  private _id = inject(IdGenerator).getId('emperor-input-');
  /**
   * HTML id for underlying control
   *
   * Uses a generated id if none is supplied by the input
   */
  readonly inputId = input<string, string | null | undefined>(this._id, {
    transform: id => id || this._id,
  });

  /**
   * Text that is displayed below the input box.
   * Hint text are sanitized to render into HTML
   */
  @Input() hint: string | null;
  /**
   * Text that is displayed below the input box with an icon.
   * Hint text are sanitized to render into HTML
   *
   * If both hint and hintWithIcon are defined, hintWithIcon takes precedence and hint will not display
   */
  @Input() hintWithIcon: IHintWithIcon | undefined;
  /**
   * BP unique question or answer identifier.
   *
   * Used to see if a component has been reused on the page.
   */
  @Input() code: string | null;
  /**
   * Used to make input readonly via marking the formcontrol disabled.
   */
  @Input() isReadOnly: boolean;
  /**
   * Removes the subscript wrapper so it doesn't take up space below the checkbox.
   * Can be added by consuming applications when hints and errors are not used
   */
  // Double negative on excludeSubscript is not ideal but allows consuming applications
  // to use `excludeSubscript` rather than something like `includeSubscript="false"`
  @Input({ transform: coerceBooleanProperty }) excludeSubscript = false;
  /**
   * Decorates label with (Optional) label
   */
  @Input() isOptional = false;
  /**
   * Emits input focus
   */
  @Output() focus = new EventEmitter();
  /**
   * Emit input blur
   */
  @Output() blur = new EventEmitter();

  labelId = computed(() => `${this.inputId()}_label`);

  constructor(private injector: Injector, private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.control = this.getControl() as FormControl;
    this.sanitizeHint();
    this.updateQaAttributeHook();
    this.generateLabelAsHtml();
  }

  /**
   * Watches for changes to formControl and formControlName to refetch the control.
   * Watches for changes in label to determine the label position
   * Watches for changes in hint to re-sanitize.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.formControl && !changes.formControl.firstChange) ||
      (changes.formControlName && !changes.formControlName.firstChange)
    ) {
      this.control = this.getControl() as FormControl;
    }

    if (changes.label && !changes.label.firstChange) {
      this.generateLabelAsHtml();
    }

    if (changes.hint && !changes.hint.firstChange) {
      this.sanitizeHint();
    }

    if (changes.id && !changes.id.firstChange) {
      this.updateQaAttributeHook();
    }

    if (changes.code && !changes.code.firstChange) {
      this.updateQaAttributeHook();
    }
    if (changes.isReadOnly) {
      this.handleIsReadOnlyChange(
        changes.isReadOnly.currentValue,
        changes.isReadOnly.previousValue,
      );
    }
  }

  /**
   * Return the formControl of the parent. Either through assignment of the
   * passed in form control, or getting it from the control container via formControlName
   * @returns
   */
  getControl(): AbstractControl | null | undefined {
    if (this.formControl) {
      return this.formControl;
    } else if (this.formControlName) {
      const controlContainer = this.injector.get(ControlContainer);
      return controlContainer.control?.get(this.formControlName);
    }
  }

  /**
   * ControlValueAccessor function
   *
   * @param fn
   */
  registerOnTouched(fn: unknown): void {
    this.formControlDirective.valueAccessor?.registerOnTouched(fn);
  }

  /**
   * ControlValueAccessor function
   *
   * @param fn
   */
  registerOnChange(fn: unknown): void {
    this.formControlDirective.valueAccessor?.registerOnChange(fn);
  }

  /**
   * ControlValueAccessor function
   *
   * @param obj
   */
  writeValue(obj: unknown): void {
    this.formControlDirective.valueAccessor?.writeValue(obj);
  }

  /**
   * ControlValueAccessor function
   *
   * @param isDisabled
   */
  setDisabledState(isDisabled: boolean): void {
    if (this.formControlDirective?.valueAccessor?.setDisabledState) {
      this.formControlDirective.valueAccessor.setDisabledState(isDisabled);
    }
  }

  onChange = (_value: unknown) => {};
  onTouch = () => {};

  protected cleanValue(value: string): string {
    return value;
  }

  onBlur() {
    this.blur.emit();
    this.control.updateValueAndValidity();
    this.onTouch();
  }

  onFocus() {
    this.focus.emit();
  }

  private sanitizeHint() {
    if (this.hint) {
      this.sanitizer.sanitize(SecurityContext.HTML, this.hint);
    }
  }

  private generateLabelAsHtml(): void {
    const newLabel = this.isOptional ? `${this.label} <span class="optional-label">(Optional)</span>` : this.label;
    this.labelAsHtml = this.sanitizer.sanitize(SecurityContext.HTML, newLabel) || '';
  }

  private updateQaAttributeHook(): void {
    const id = this.inputId();
    if (this.code && id) {
      this.qaAttributeHook = `${this.code}-${id}`;
    }
  }

  private handleIsReadOnlyChange(newValue, prevValue): void {
    if (newValue == prevValue) {
      return;
    }
    const currentState = this.getControl()?.disabled;
    const isReadOnly = !!newValue;
    if (isReadOnly === currentState) {
      return;
    }
    isReadOnly ? this.getControl()?.disable() : this.getControl()?.enable();
  }
}
